import {
  compareTimestampsWithMicrosecondPrecision,
  includesEnumValue,
  type MinimalSubscriptionResponse,
  type Order,
} from '@talos/kyoko';
import { OPEN_ORDER_STATUSES } from './constants';

/**
 * Reducer for the merged RECENT_ORDERS + OPEN_ORDERS stream in OrdersProvider.
 *
 * The start of an OPEN_ORDERS initial snapshot is the server's authoritative list of
 * currently-open orders. Any locally-held entry with an open status that is absent from
 * the snapshot has transitioned to a closed status while we weren't listening (e.g. across
 * a disconnect), and must be evicted from the Map. Entries with closed statuses (seeded by
 * RECENT_ORDERS) are preserved so the UI can still render their terminal state.
 *
 * Note: upstream wsWaitForAllPages guarantees that paged OPEN_ORDERS messages are coalesced
 * into a single initial message before reaching this function.
 */
export function ordersProviderMergeOrderUpdate(
  orders: Map<string, Order>,
  json: MinimalSubscriptionResponse<Order>
): Map<string, Order> {
  const isFreshOpenOrdersSnapshot = json.tag === 'OPEN_ORDERS' && json.initial === true;

  if (isFreshOpenOrdersSnapshot) {
    // When an initial Open Orders message comes in, clear all Open Orders in memory.
    // They will be replaced with the contents of the incoming message.
    for (const [id, existing] of orders) {
      if (includesEnumValue(OPEN_ORDER_STATUSES, existing.OrdStatus)) {
        orders.delete(id);
      }
    }
  }

  for (const d of json.data ?? []) {
    const existing = orders.get(d.OrderID);
    // If an existing order exists with the same revision, only update it if the revision is newer or
    // equal with later timestamp.
    // This is because exectype=restated does not update revision #.
    // If the revision is larger always update as under load we cannot trust Timestamps to be in order.
    if (existing == null || d.Revision > existing.Revision) {
      orders.set(d.OrderID, d);
    } else if (
      d.Revision === existing.Revision &&
      (!d.Timestamp ||
        !existing.Timestamp ||
        compareTimestampsWithMicrosecondPrecision(d.Timestamp, existing.Timestamp) >= 0)
    ) {
      orders.set(d.OrderID, d);
    }
  }

  return orders;
}
